import React, { useState, memo, Component } from 'react';
import { Link } from 'react-router-dom';
import { getRandomColor } from '../utils/colors';
import { useRandomLinkColors } from '../utils/useRandomLinkColors';
import { ChevronLeft, ChevronRight } from 'lucide-react';
// Reusable Slideshow Component
const Slideshow = ({
  images,
  captions,
  projectTitle,
  description
}: {
  images: string[];
  captions: {
    title: string;
    medium: string;
  }[];
  projectTitle: string;
  description: React.ReactNode;
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const nextSlide = () => {
    setCurrentIndex(prev => prev === images.length - 1 ? 0 : prev + 1);
  };
  const prevSlide = () => {
    setCurrentIndex(prev => prev === 0 ? images.length - 1 : prev - 1);
  };
  return <div className="mb-32" data-id="element-407">
      {/* Slideshow Container */}
      <div className="relative w-full max-w-4xl mx-auto mb-6 group" data-id="element-408">
        {/* Main Image */}
        <div className="relative aspect-[4/3] md:aspect-[3/2] w-full flex items-center justify-center cursor-pointer bg-[#faf9f7]" onClick={nextSlide} data-id="element-409">
          <img src={images[currentIndex]} alt={`${projectTitle} - ${captions[currentIndex]?.title || `Image ${currentIndex + 1}`}`} className="max-w-full max-h-[70vh] object-contain transition-opacity duration-300" data-id="element-410" />
        </div>

        {/* Navigation Arrows - Visible on hover for desktop, always for mobile */}
        <button onClick={e => {
        e.stopPropagation();
        prevSlide();
      }} className="absolute left-0 top-1/2 -translate-y-1/2 p-4 text-[#333333] opacity-0 group-hover:opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity duration-200 focus:outline-none" aria-label="Previous image" data-id="element-411">
          <ChevronLeft className="w-8 h-8 font-thin" strokeWidth={1} data-id="element-412" />
        </button>

        <button onClick={e => {
        e.stopPropagation();
        nextSlide();
      }} className="absolute right-0 top-1/2 -translate-y-1/2 p-4 text-[#333333] opacity-0 group-hover:opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity duration-200 focus:outline-none" aria-label="Next image" data-id="element-413">
          <ChevronRight className="w-8 h-8 font-thin" strokeWidth={1} data-id="element-414" />
        </button>

        {/* Mobile Navigation */}
        <div className="md:hidden absolute inset-x-0 top-1/2 -translate-y-1/2 flex justify-between px-2 pointer-events-none" data-id="element-415">
          <div className="pointer-events-auto p-2" onClick={e => {
          e.stopPropagation();
          prevSlide();
        }} data-id="element-416">
            <ChevronLeft className="w-6 h-6 text-[#333333]" strokeWidth={1} data-id="element-417" />
          </div>
          <div className="pointer-events-auto p-2" onClick={e => {
          e.stopPropagation();
          nextSlide();
        }} data-id="element-418">
            <ChevronRight className="w-6 h-6 text-[#333333]" strokeWidth={1} data-id="element-419" />
          </div>
        </div>
      </div>

      {/* Caption & Info */}
      <div className="max-w-2xl mx-auto text-center" data-id="element-420">
        <div className="text-xs mb-4 font-sans tracking-wide text-gray-500" data-id="element-421">
          {currentIndex + 1} / {images.length}
        </div>

        <div className="text-sm italic mb-8 space-y-1" data-id="element-422">
          <div data-id="element-423">{captions[currentIndex]?.title}</div>
          <div data-id="element-424">{captions[currentIndex]?.medium}</div>
        </div>

        <div className="text-base leading-relaxed text-left" data-id="element-425">{description}</div>
      </div>
    </div>;
};
const PortfolioPage = () => {
  useRandomLinkColors();
  const bodyImages = ['https://cdn.magicpatterns.com/uploads/u8fb8k7ZVyUaSp39VegeSF/Reva_(Playa_del_Ray,_California).jpg', 'https://cdn.magicpatterns.com/uploads/jsH2yNinckPotf7KdUBP4T/Root_Systems_(Austin,_Texas).jpg', 'https://cdn.magicpatterns.com/uploads/aGsE8k4iRYYthT35PXzsKH/000342810006.jpg', 'https://cdn.magicpatterns.com/uploads/aqhbD3ufMxkMF8Ku2Ae4iV/000865080009.jpg', 'https://cdn.magicpatterns.com/uploads/jiCZsaPSgyvo256NfB1EgS/8_Morlet_Madonna_(Silverlake,_Los_Angeles).jpg', 'https://cdn.magicpatterns.com/uploads/mTmjy5UeiZxDBYjkQrnhe7/Coco_and_Blue_(Echo_Park,_Los_Angeles).jpg', 'https://cdn.magicpatterns.com/uploads/sULHm2kNafrRda6kwMQnki/Marley_and_Josh_(Santa_Barbara,_California).jpg', 'https://cdn.magicpatterns.com/uploads/pshoeaizKAH2Sn99sEyxSp/Amanda,_Oona_and_Cosimo_(Topanga,_California).jpg', 'https://cdn.magicpatterns.com/uploads/sxCozqopXzMx3MT2dGSRC7/The_Chateau_(West_Hollywood,_California).jpg', 'https://cdn.magicpatterns.com/uploads/85CsuFxSJUKtbYkHZynT3i/10_Morlet_Nori_(Inglewood,_California).jpg'];
  const bodyCaptions = [{
    title: 'Reva (Playa Del Ray, Los Angeles), 2022.',
    medium: 'Traditional c-type print from 35mm negative.'
  }, {
    title: 'Root Systems (Austin, Texas), 2022.',
    medium: 'Traditional c-type print from 35mm negative.'
  }, {
    title: 'Two White Cars (Hollywood Hills, Los Angeles), 2022.',
    medium: 'Traditional c-type print from medium format negative.'
  }, {
    title: 'Domestic Scene (Santa Barbara, California), 2022.',
    medium: 'Traditional c-type print from medium format negative.'
  }, {
    title: 'Madonna (Silverlake, Los Angeles), 2022.',
    medium: 'Traditional c-type print from medium format negative.'
  }, {
    title: 'Coco and Blue (Silverlake, Los Angeles), 2022.',
    medium: 'Traditional c-type print from 35mm negative.'
  }, {
    title: 'Marley and Josh (Santa Monica, California), 2022.',
    medium: 'Traditional c-type print from 35mm negative.'
  }, {
    title: 'Oona and Her Baby Brother (Topanga, Los Angeles), 2022.',
    medium: 'Traditional c-type print from 35mm negative.'
  }, {
    title: 'The Chateaux (West Hollywood, California), 2022.',
    medium: 'Traditional c-type print from medium format negative.'
  }, {
    title: 'Nori (Inglewood, California), 2022.',
    medium: 'Traditional c-type print from 35mm negative.'
  }];
  const promiseImages = ['https://uploadthingy.s3.us-west-1.amazonaws.com/hidkZE7pnuDdsYunj1wZ11/Madeleine_Morlet_1.jpg', 'https://uploadthingy.s3.us-west-1.amazonaws.com/mP3BR1QUjfT2XqWiEGYhbb/Madeleine_Morlet_3.jpg', 'https://uploadthingy.s3.us-west-1.amazonaws.com/fuL5gRuHQ6o4ukBGcyeC4x/Madeleine_Morlet_4.jpg', 'https://uploadthingy.s3.us-west-1.amazonaws.com/iTtKjejxZ8gXbq4c2FSZDc/Madeleine_Morlet_5.jpg', 'https://uploadthingy.s3.us-west-1.amazonaws.com/w2VR4kTDevX9g4VWqy75cY/Madeleine_Morlet_7.jpg', 'https://uploadthingy.s3.us-west-1.amazonaws.com/oboqfQeRz1hrKyvhiJ8C2t/Madeleine_Morlet_8.jpg', 'https://uploadthingy.s3.us-west-1.amazonaws.com/54NunmgGGBiT8H5jXwbosk/Madeleine_Morlet_9.jpg', 'https://uploadthingy.s3.us-west-1.amazonaws.com/dzhUR5M9ixAH3psDyVQZED/Madeleine_Morlet_15.jpg', 'https://uploadthingy.s3.us-west-1.amazonaws.com/4hm8HENkSXGA4FzLB1XQhG/Madeleine_Morlet_19.jpg', 'https://uploadthingy.s3.us-west-1.amazonaws.com/8qWuSgCSu69DPRJH7a1YxW/Madeleine_Morlet_20.jpg'];
  // ... existing code ...
  return <div className="min-h-screen bg-[#faf9f7] text-[#333333] font-serif" data-id="element-426">
      {/* Header */}
      <header className="fixed top-0 w-full flex justify-end px-6 py-6 z-50 bg-[#faf9f7]/90 backdrop-blur-sm" data-id="element-427">
        <div className="flex space-x-6" data-id="element-428">
          <div className="text-base font-normal" data-id="element-429">
            <Link to="/" className="transition-colors duration-200" onMouseEnter={e => {
            e.currentTarget.style.color = getRandomColor();
          }} onMouseLeave={e => {
            e.currentTarget.style.color = '#333333';
          }} data-id="element-430">
              Madeleine Morlet
            </Link>
          </div>
          <div className="text-base font-normal" data-id="element-431">
            <Link to="/info" className="transition-colors duration-200" onMouseEnter={e => {
            e.currentTarget.style.color = getRandomColor();
          }} onMouseLeave={e => {
            e.currentTarget.style.color = '#333333';
          }} data-id="element-432">
              Info
            </Link>
          </div>
        </div>
      </header>

      <main className="pt-32 pb-24 px-6 max-w-4xl mx-auto" data-id="element-433">
        {/* Intro Text */}
        <div className="mb-32 text-base leading-relaxed max-w-2xl mx-auto" data-id="element-434">
          <div className="font-bold underline mb-6" data-id="element-435">Example</div>
          <p data-id="element-436">
            This page provides contextual material to support my Arts Council
            Research & Development application. The images shown are from
            previous photographic projects and reflect my visual language and
            working methods. The proposed R&D period will build on this practice
            while exploring new technical and conceptual directions.
          </p>
        </div>

        {/* Slideshow 1: The Body Is Not A Thing */}
        <div className="max-w-2xl mx-auto mb-8" data-id="element-437">
          <div className="text-base" data-id="element-438">
            <span className="font-bold" data-id="element-439">Project Title:</span>{' '}
            <em data-id="element-440">The Body Is Not A Thing</em>
          </div>
          <div className="text-base" data-id="element-441">
            <span className="font-bold" data-id="element-442">Project Year:</span> 2022
          </div>
        </div>
        <Slideshow images={bodyImages} captions={bodyCaptions} projectTitle="The Body Is Not A Thing" description={<>
              <span className="font-bold" data-id="element-444">Project Statement:</span>{' '}
              <em data-id="element-445">The Body Is Not A Thing</em> was conceived during lockdown.
              Like many women with young children, I experienced a loss of
              autonomy. In America, where I was living, the appetite for
              regressive politics was growing. I felt frustrated with the
              unrelatable and sanitised imagery that dominates mainstream
              representations of motherhood. In the year Roe V. Wade was
              overturned I travelled from my home, a small rural community in
              New England, to Los Angeles. I found myself drawn to the supposed
              duality of our sexual desire and our ability to give birth. I
              photographed actors, dancers, sex workers, and mothers. I thought
              I understood how to cast the female gaze. Later, as I reviewed the
              images, I found myself confronted by the enduring influence of the
              male gaze. I am forced to acknowledge the historical associations
              of female nudity, the provocation of shame, the influence of
              pornography, and internalised misogyny. Observing women
              intimately, interplaying sexuality and motherhood, is
              uncomfortable–but should it be?
            </>} data-id="element-443" />

        {/* Slideshow 2: I Promise I'll Never Forget */}
        <div className="max-w-2xl mx-auto mb-8" data-id="element-446">
          <div className="text-base" data-id="element-447">
            <span className="font-bold" data-id="element-448">Project Title:</span>{' '}
            <em data-id="element-449">I Promise I'll Never Forget</em>
          </div>
          <div className="text-base" data-id="element-450">
            <span className="font-bold" data-id="element-451">Project Year:</span> 2018-2021
          </div>
        </div>
        <Slideshow images={promiseImages} captions={[{
        title: 'Tennis Whites (Portland, Maine), 2020.',
        medium: 'Traditional c-type print from medium-format negative.'
      }, {
        title: 'Sea Shell Necklace (Appleton, Maine), 2020.',
        medium: 'Traditional c-type print from medium-format negative.'
      }, {
        title: 'Grazed Heart (Rockport, Maine), 2020.',
        medium: 'Traditional c-type print from medium-format negative.'
      }, {
        title: 'Your Un-Dumb Letters (Camden, Maine), 2020.',
        medium: 'Traditional c-type print from medium-format negative.'
      }, {
        title: 'Veronica (Lincolnville, Maine), 2021.',
        medium: 'Traditional c-type print from medium-format negative.'
      }, {
        title: 'Crimson Crisp (Warren, Maine), 2021.',
        medium: 'Traditional c-type print from medium-format negative.'
      }, {
        title: 'A Room of Their Own (Rockland, Maine), 2019.',
        medium: 'Traditional c-type print from medium-format negative.'
      }, {
        title: 'Walking in the Dark (Portland, Maine), 2021.',
        medium: 'Traditional c-type print from medium-format negative.'
      }, {
        title: 'The Kiss (Camden, Maine), 2020.',
        medium: 'Traditional c-type print from medium-format negative.'
      }, {
        title: 'Young Philosopher (Portland, Maine), 2021.',
        medium: 'Traditional c-type print from medium-format negative.'
      }]} projectTitle="I Promise I'll Never Forget" description={<>
              <span className="font-bold" data-id="element-453">Project Statement:</span>{' '}
              <em data-id="element-454">I Promise I'll Never Forget</em> is a love letter to youth.
              This dramatisation of adolescence serves to examine identity and
              personal relationships through the construction of imaginative
              narratives. These images have been made in mid-coast Maine,
              reflecting on how our uncertain and self-conscious times are not
              met alone, but amongst our communities. They hold a mirror to my
              own coming of age, as a foreigner and new mother, experiences that
              have been punctuated by a youthful ambition for friendship and
              concurrent feelings of isolation. How daring it is to make such
              extravagant promises, knowing that memories are as fleeting as
              life itself.
            </>} data-id="element-452" />
      </main>
    </div>;
};
export default PortfolioPage;